import * as DialogPrimitive from "@radix-ui/react-dialog";
import { FocusScope } from "@radix-ui/react-focus-scope";
import { Button } from "../components/Button";
import { ScrollArea } from "../components/ScrollArea";
import { Separator } from "../components/Separator";
import { XClose } from "../icons/v2-stroke";
import { cn } from "../lib/utils";
import { cva } from "class-variance-authority";
import * as React from "react";

/**
 * A modal surface that interrupts the flow to focus the user on a single task
 * or decision — confirmations, short forms, or tool-permission prompts.
 * Compose it from DialogTrigger, DialogContent, DialogHeader (with
 * DialogTitle / DialogDescription), DialogContainer, and DialogFooter; for
 * multi-step flows use MultiPageDialog, and for non-blocking contextual
 * information use ContentMessage.
 * DialogContent defaults preventAutoFocusOnClose to true; set it to false
 * to return focus to a trigger. For controlled surfaces without a trigger,
 * onCloseAutoFocus can restore the opening control explicitly.
 * @summary Modal dialog for focused tasks and confirmations.
 */
const Dialog = DialogPrimitive.Root;
/** Element that opens the dialog (use asChild to wrap a Button). */
const DialogTrigger = DialogPrimitive.Trigger;
/** Element that closes the dialog when activated. */
const DialogClose = DialogPrimitive.Close;
/** Portals the dialog content to another part of the DOM. */
const DialogPortal = DialogPrimitive.Portal;

/** Dimmed backdrop rendered behind the dialog content. */
const DialogOverlay = React.forwardRef(({
  className,
  ...props
}, ref) => /*#__PURE__*/React.createElement(DialogPrimitive.Overlay, {
  className: cn("fixed inset-0 z-50 duration-200 ease-emphasized data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:duration-150 motion-reduce:animate-none", "bg-muted-foreground/75 dark:bg-muted-background/75", className),
  ...props,
  ref: ref
}));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;
const DIALOG_SIZES = ["md", "lg", "xl", "2xl", "full", "fit"];
const DIALOG_HEIGHTS = ["md", "lg", "xl", "2xl"];
const sizeClasses = {
  md: "sm:max-w-md",
  lg: "sm:max-w-xl",
  xl: "sm:max-w-3xl",
  "2xl": "sm:max-w-5xl",
  full: "sm:max-w-full sm:h-full",
  fit: "sm:max-w-[90vw] w-fit"
};
const heightClasses = {
  md: "sm:h-md",
  lg: "sm:h-lg",
  xl: "sm:h-xl",
  "2xl": "sm:h-2xl"
};

// With grow, the fixed height becomes a minimum: the h-* class is not
// emitted and min-h keeps the height's size as the floor. min-height wins
// over max-height in CSS, so the floor is itself capped at 90vh to never
// exceed the base max-h-[90vh] on short viewports.
const growHeightClasses = {
  md: "sm:min-h-[min(448px,90vh)]",
  lg: "sm:min-h-[min(576px,90vh)]",
  xl: "sm:min-h-[min(768px,90vh)]",
  "2xl": "sm:min-h-[min(1024px,90vh)]"
};
const DIALOG_VARIANTS = ["default", "command"];
const variantClasses = {
  default: cn("top-[50%] translate-y-[-50%] duration-200 ease-emphasized data-[state=closed]:duration-150 motion-reduce:animate-none", "data-[state=open]:animate-in data-[state=closed]:animate-out", "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0", "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"),
  command: cn("top-[20%] duration-200 ease-emphasized data-[state=closed]:duration-150 motion-reduce:animate-none", "data-[state=open]:animate-in data-[state=closed]:animate-out", "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0", "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95", "data-[state=open]:slide-in-from-top-2 data-[state=closed]:slide-out-to-top-2")
};
const dialogVariants = cva(cn("fixed left-[50%] z-50 overflow-hidden translate-x-[-50%]", "rounded-2xl flex flex-col w-full max-w-[calc(100vw-2rem)] border border shadow-lg", "bg-modal-background", "border-border", "max-h-[90vh]",
// Radix focuses the panel itself when nothing tabbable is inside or on
// background clicks; suppress the UA focus ring on the panel.
"focus:outline-none"), {
  variants: {
    size: sizeClasses,
    height: heightClasses,
    variant: variantClasses
  },
  defaultVariants: {
    size: "md",
    variant: "default"
  }
});
/** The dialog panel itself: portal, overlay, and the sized, animated content box. */
const DialogContent = React.forwardRef(({
  className,
  children,
  size,
  height,
  grow,
  variant,
  trapFocusScope,
  isAlertDialog,
  preventAutoFocusOnClose = true,
  onCloseAutoFocus,
  mountPortalContainer,
  ...props
}, ref) => {
  const handleCloseAutoFocus = React.useCallback(event => {
    if (preventAutoFocusOnClose) {
      event.preventDefault();
    }
    onCloseAutoFocus === null || onCloseAutoFocus === void 0 || onCloseAutoFocus(event);
  }, [preventAutoFocusOnClose, onCloseAutoFocus]);
  return /*#__PURE__*/React.createElement(DialogPortal, {
    container: mountPortalContainer
  }, /*#__PURE__*/React.createElement(DialogOverlay, null), /*#__PURE__*/React.createElement(FocusScope, {
    trapped: trapFocusScope,
    asChild: true
  }, /*#__PURE__*/React.createElement(DialogPrimitive.Content, {
    ref: ref,
    className: cn(
    // grow replaces the fixed height with a floor.
    dialogVariants({
      size,
      height: grow ? undefined : height,
      variant
    }), grow && height ? growHeightClasses[height] : undefined, className),
    onInteractOutside: isAlertDialog ? e => e.preventDefault() : props.onInteractOutside,
    onCloseAutoFocus: handleCloseAutoFocus,
    ...props
  }, children)));
});
DialogContent.displayName = DialogPrimitive.Content.displayName;
/** Sticky header area holding DialogTitle / DialogDescription and a built-in close button. */
const DialogHeader = ({
  className,
  children,
  buttonSize = "mini",
  buttonVariant = "ghost",
  hideButton = false,
  ...props
}) => /*#__PURE__*/React.createElement("div", {
  className: cn("sticky top-0 z-50 flex flex-none flex-col gap-0 bg-modal-background px-5 pt-4 text-left", className),
  ...props
}, children, !hideButton && /*#__PURE__*/React.createElement(DialogClose, {
  asChild: true,
  className: "absolute right-3 top-3"
}, /*#__PURE__*/React.createElement(Button, {
  icon: XClose,
  variant: buttonVariant,
  size: buttonSize
})));
DialogHeader.displayName = "DialogHeader";
/** Scrollable body of the dialog, with an optional fixed section on top. */
const DialogContainer = ({
  children,
  fixedContent,
  className
}) => {
  const contentStyles = cn("copy-base break-words px-5 py-4 text-foreground");
  const scrollableContent = /*#__PURE__*/React.createElement(ScrollArea, {
    className: "w-full flex-grow"
  }, /*#__PURE__*/React.createElement("div", {
    className: cn(contentStyles, "relative flex flex-col gap-2 text-left", className)
  }, children));
  if (fixedContent) {
    return /*#__PURE__*/React.createElement("div", {
      className: "flex flex-grow flex-col overflow-hidden"
    }, /*#__PURE__*/React.createElement("div", {
      className: cn(contentStyles, "flex-none")
    }, fixedContent), /*#__PURE__*/React.createElement(Separator, null), scrollableContent);
  }
  return scrollableContent;
};
DialogContainer.displayName = "DialogContainer";
/** Footer row of action buttons, right-aligned; label buttons with the action they perform. */
const DialogFooter = ({
  className,
  children,
  leftButtonProps,
  rightButtonProps,
  dialogCloseClassName,
  ...props
}) => /*#__PURE__*/React.createElement("div", {
  className: "flex flex-none flex-col gap-0"
}, /*#__PURE__*/React.createElement("div", {
  className: cn("flex flex-none flex-row justify-end gap-2 px-3 pb-3 pt-2", className),
  ...props
}, leftButtonProps && (leftButtonProps.disabled ? /*#__PURE__*/React.createElement(Button, {
  ...leftButtonProps
}) : /*#__PURE__*/React.createElement(DialogClose, {
  className: dialogCloseClassName,
  asChild: true
}, /*#__PURE__*/React.createElement(Button, {
  ...leftButtonProps
}))), rightButtonProps && (rightButtonProps.disabled ? /*#__PURE__*/React.createElement(Button, {
  ...rightButtonProps
}) : /*#__PURE__*/React.createElement(DialogClose, {
  className: dialogCloseClassName,
  asChild: true
}, /*#__PURE__*/React.createElement(Button, {
  ...rightButtonProps
}))), children));
DialogFooter.displayName = "DialogFooter";

/** Dialog heading, with an optional leading visual (e.g. an Avatar or logo). */
const DialogTitle = React.forwardRef(({
  className,
  visual,
  children,
  ...props
}, ref) => /*#__PURE__*/React.createElement("div", {
  className: "flex flex-row items-center gap-2 pt-1"
}, visual, /*#__PURE__*/React.createElement(DialogPrimitive.Title, {
  ref: ref,
  className: cn("heading-lg", "min-w-0 break-words", "text-foreground", className),
  ...props
}, children)));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

/** Muted supporting text under the DialogTitle. */
const DialogDescription = React.forwardRef(({
  className,
  ...props
}, ref) => /*#__PURE__*/React.createElement(DialogPrimitive.Description, {
  ref: ref,
  className: cn("copy-sm", "text-muted-foreground", className),
  ...props
}));
DialogDescription.displayName = DialogPrimitive.Description.displayName;
export { Dialog, DialogClose, DialogContainer, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogOverlay, DialogPortal, DialogTitle, DialogTrigger };
//# sourceMappingURL=Dialog.js.map